import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  Input,
  Spinner,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  FiArrowLeft,
  FiDownload,
  FiMail,
  FiPlayCircle,
  FiRefreshCw,
  FiTrash2,
} from "react-icons/fi"

import { OpenAPI } from "@/client"
import { IconBox, VisionButton, VisionCard } from "@/components/VisionUI"
import {
  DialogActionTrigger,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/components/ui/dialog"
import useCustomToast from "@/hooks/useCustomToast"
import { useSemanticColors } from "@/hooks/useSemanticColors"

type DemoSettingsResponse = {
  enabled: boolean
  purge_interval_hours: number
  banner_message_key: string
  banner_left_message?: string | null
  banner_right_message?: string | null
  org_id: string | null
  last_purge_at: string | null
  next_purge_at: string | null
  visitor_cap: number
  tag_cap_per_org: number
  reader_cap_per_org: number
  // Optional on every mutation response — present when the server caught
  // a failure mid-flight. `enabled` still reflects the real state, so
  // the UI can branch on that and surface `error.message` in a toast.
  error?: { code: string; message: string }
  stats?: Record<string, number>
}

class DemoMutationError extends Error {
  code: string
  enabled: boolean
  constructor(code: string, message: string, enabled: boolean) {
    super(message)
    this.code = code
    this.enabled = enabled
  }
}

async function parseDemoResponse(r: Response): Promise<DemoSettingsResponse> {
  // The API is wrapped so it always answers with the settings shape, even
  // on failure. Treat a non-2xx as a hard error, then look for the optional
  // `error` envelope on a 2xx response to surface graceful failures.
  if (!r.ok) {
    throw new Error(`Request failed: ${r.status}`)
  }
  const data = (await r.json()) as DemoSettingsResponse
  if (data.error) {
    throw new DemoMutationError(
      data.error.code,
      data.error.message,
      data.enabled,
    )
  }
  return data
}

type PurgeLogRow = {
  id: string
  at: string
  user_id: string | null
  metadata: Record<string, unknown>
}

type ReaderConsentRow = {
  reader_id: string
  email: string | null
  opt_in: boolean
  opt_in_at: string | null
  source: string | null
  reader_name: string | null
}

// RFC-4180-ish CSV cell: wrap in quotes and double any embedded quotes
// whenever the value contains a comma, quote, or newline.
function csvCell(value: unknown): string {
  const s = value == null ? "" : String(value)
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

function apiFetch(path: string, init: RequestInit = {}) {
  const token = localStorage.getItem("access_token")
  const base = (OpenAPI.BASE || "").replace(/\/$/, "")
  return fetch(`${base}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  })
}

export const Route = createFileRoute("/_layout/admin-demo")({
  component: AdminDemo,
})

function AdminDemo() {
  const sc = useSemanticColors()
  const { t } = useTranslation("admin")
  const { t: tDemo } = useTranslation("demo")
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showToast } = useCustomToast()

  const settingsQuery = useQuery({
    queryKey: ["admin-demo-settings"],
    queryFn: () =>
      apiFetch("/api/v1/admin/demo").then((r) => {
        if (!r.ok) throw new Error("Failed to load demo settings")
        return r.json() as Promise<DemoSettingsResponse>
      }),
    refetchInterval: 30_000,
  })

  const purgeLogQuery = useQuery({
    queryKey: ["admin-demo-purge-log"],
    queryFn: () =>
      apiFetch("/api/v1/admin/demo/purge-log?limit=50").then((r) => {
        if (!r.ok) throw new Error("Failed to load purge log")
        return r.json() as Promise<PurgeLogRow[]>
      }),
    refetchInterval: 30_000,
  })

  const consentsQuery = useQuery({
    queryKey: ["admin-demo-reader-consents"],
    queryFn: () =>
      apiFetch("/api/v1/admin/demo/reader-consents").then((r) => {
        if (!r.ok) throw new Error("Failed to load reader consents")
        return r.json() as Promise<ReaderConsentRow[]>
      }),
    refetchInterval: 30_000,
  })

  const downloadConsentsCsv = () => {
    const rows = consentsQuery.data ?? []
    const header = [
      "email",
      "opt_in",
      "opt_in_at",
      "source",
      "reader_name",
      "reader_id",
    ]
    const body = rows.map((r) =>
      [
        r.email ?? "",
        r.opt_in ? "true" : "false",
        r.opt_in_at ?? "",
        r.source ?? "",
        r.reader_name ?? "",
        r.reader_id,
      ]
        .map(csvCell)
        .join(","),
    )
    const csv = [header.join(","), ...body].join("\r\n")
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    const stamp = new Date().toISOString().slice(0, 10)
    a.href = url
    a.download = `tapdpp-reader-consents-${stamp}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Draft state for the form inputs — applied via the PUT mutation.
  const [draftInterval, setDraftInterval] = useState<string>("")
  // Banner free-text slots that flank the HH:MM:SS countdown in the
  // yellow strip. Operator overrides; falls back to the SPA's i18n
  // defaults when left empty. The old single "banner_message_key"
  // field was redundant with these two slots and has been removed
  // from the UI (the backend column still exists as harmless dead
  // weight from an older migration).
  const [draftBannerLeft, setDraftBannerLeft] = useState<string>("")
  const [draftBannerRight, setDraftBannerRight] = useState<string>("")
  // Timer-extend control — operator picks an amount (or types one) and
  // hits "Extend".
  const [extendMinutes, setExtendMinutes] = useState<string>("60")
  // Modal visibility for the enable / disable informational dialogs.
  const [enableDialogOpen, setEnableDialogOpen] = useState(false)
  const [disableDialogOpen, setDisableDialogOpen] = useState(false)

  const enableMutation = useMutation({
    mutationFn: () =>
      apiFetch("/api/v1/admin/demo/enable", { method: "POST" }).then(
        parseDemoResponse,
      ),
    onSuccess: async () => {
      // refetchQueries (not just invalidate) so the demo-banner cache
      // updates on the same tick and the global banner appears
      // immediately, instead of waiting for the 60s poll.
      await queryClient.refetchQueries({ queryKey: ["demo-banner"] })
      queryClient.invalidateQueries({ queryKey: ["admin-demo-settings"] })
      showToast(
        tDemo("admin.enabledToastTitle", "Demo mode enabled"),
        tDemo(
          "admin.enabledToastBody",
          "Anonymous visitors can now access this cluster. The yellow demo banner is now visible to all users.",
        ),
        "success",
      )
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : String(err)
      showToast(
        tDemo("admin.enableErrorTitle", "Failed to enable demo mode"),
        msg,
        "error",
      )
    },
  })
  const disableMutation = useMutation({
    mutationFn: () =>
      apiFetch("/api/v1/admin/demo/disable", { method: "POST" }).then(
        parseDemoResponse,
      ),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ["demo-banner"] })
      queryClient.invalidateQueries({ queryKey: ["admin-demo-settings"] })
      showToast(
        tDemo("admin.disabledToastTitle", "Demo mode disabled"),
        tDemo(
          "admin.disabledToastBody",
          "The cluster is back to normal authentication. Existing visitor sessions have been ended.",
        ),
        "success",
      )
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : String(err)
      showToast(
        tDemo("admin.disableErrorTitle", "Failed to disable demo mode"),
        msg,
        "error",
      )
    },
  })
  const purgeMutation = useMutation({
    mutationFn: () =>
      apiFetch("/api/v1/admin/demo/purge-now", { method: "POST" }).then(
        parseDemoResponse,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-demo-settings"] })
      queryClient.invalidateQueries({ queryKey: ["admin-demo-purge-log"] })
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : String(err)
      showToast(tDemo("admin.purgeErrorTitle", "Purge failed"), msg, "error")
    },
  })
  const updateMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch("/api/v1/admin/demo", {
        method: "PUT",
        body: JSON.stringify(body),
      }).then((r) => {
        if (!r.ok) throw new Error("Failed to save")
        return r.json()
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-demo-settings"] })
      queryClient.invalidateQueries({ queryKey: ["demo-banner"] })
    },
    onError: (err: unknown) => {
      showToast(
        tDemo("admin.saveErrorTitle", "Save failed"),
        err instanceof Error ? err.message : String(err),
        "error",
      )
    },
  })

  // Timer extension lives on its own endpoint so the operator's intent
  // ("add minutes to the current deadline") doesn't get bundled with
  // settings edits and racey with the purge monitor.
  const extendMutation = useMutation({
    mutationFn: (minutes: number) =>
      apiFetch("/api/v1/admin/demo/extend", {
        method: "POST",
        body: JSON.stringify({ minutes }),
      }).then((r) => {
        if (!r.ok)
          return r.text().then((t) => {
            throw new Error(t || `Extend failed: ${r.status}`)
          })
        return r.json()
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-demo-settings"] })
      queryClient.invalidateQueries({ queryKey: ["demo-banner"] })
      showToast(
        tDemo("admin.extendedToastTitle", "Reset deadline extended"),
        "",
        "success",
      )
    },
    onError: (err: unknown) => {
      showToast(
        tDemo("admin.extendErrorTitle", "Could not extend timer"),
        err instanceof Error ? err.message : String(err),
        "error",
      )
    },
  })

  const s = settingsQuery.data

  // Seed the banner draft inputs from whatever the server has stored
  // so the operator sees the current override (not a blank field). We
  // only sync when the server value transitions in/out of being set —
  // mid-edit typing keeps its own state.
  useEffect(() => {
    setDraftBannerLeft(s?.banner_left_message ?? "")
    setDraftBannerRight(s?.banner_right_message ?? "")
  }, [s?.banner_left_message, s?.banner_right_message])

  const nextResetCountdown = useMemo(() => {
    if (!s?.next_purge_at) return "—"
    const ms = new Date(s.next_purge_at).getTime() - Date.now()
    if (!Number.isFinite(ms) || ms <= 0) return "due now"
    const totalSeconds = Math.floor(ms / 1000)
    const h = Math.floor(totalSeconds / 3600)
    const m = Math.floor((totalSeconds % 3600) / 60)
    const sec = totalSeconds % 60
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
  }, [s?.next_purge_at])

  const applyForm = () => {
    const body: Record<string, unknown> = {}
    if (draftInterval && /^\d+$/.test(draftInterval)) {
      body.purge_interval_hours = Number(draftInterval)
    }
    // Banner slots — empty string is a valid value (clears the
    // override and falls back to the SPA's i18n default). We send the
    // field whenever the operator touched the input.
    if (draftBannerLeft !== "" || s?.banner_left_message != null) {
      body.banner_left_message = draftBannerLeft
    }
    if (draftBannerRight !== "" || s?.banner_right_message != null) {
      body.banner_right_message = draftBannerRight
    }
    if (Object.keys(body).length === 0) return
    updateMutation.mutate(body)
    setDraftInterval("")
  }

  const applyExtend = (minutesOverride?: number) => {
    const value = minutesOverride ?? Number(extendMinutes)
    if (!Number.isFinite(value) || value < 1) return
    extendMutation.mutate(value)
  }

  return (
    <Box>
      <Flex mb={4} align="center" gap={3}>
        <VisionButton
          variant="ghost"
          size="sm"
          onClick={() => navigate({ to: "/admin" })}
        >
          <FiArrowLeft />
          <Text ml={2}>{tDemo("admin.backToAdmin", "Back to Admin")}</Text>
        </VisionButton>
      </Flex>

      <Flex align="center" gap={3} mb={6} flexWrap="wrap">
        <IconBox variant="glass" size="lg">
          <FiPlayCircle fontSize="24px" color={sc.iconColor} />
        </IconBox>
        <Box flex="1">
          <Heading size="lg" color={sc.heading}>
            {tDemo("admin.title", "Demo Mode")}
          </Heading>
          <Text color={sc.textMuted}>
            {tDemo(
              "admin.subtitle",
              "Operate this cluster as a public sandbox.",
            )}
          </Text>
        </Box>
      </Flex>

      {/* Master toggle */}
      <VisionCard mb={6}>
        <VStack align="stretch" gap={4} p={5}>
          <Flex justify="space-between" align="center" gap={4} flexWrap="wrap">
            <Box flex="1">
              <Heading size="sm" color={sc.heading} mb={1}>
                {tDemo("admin.enabledLabel", "Demo mode enabled")}
              </Heading>
              <Text color={sc.textMuted} fontSize="sm">
                {tDemo(
                  "admin.enabledHelp",
                  "While enabled: login is bypassed, anonymous visitors can register readers and tags, API tokens are revoked, user management is locked.",
                )}
              </Text>
            </Box>
            <HStack gap={2}>
              {settingsQuery.isLoading ? (
                <Spinner size="sm" />
              ) : s?.enabled ? (
                <Button
                  size="md"
                  colorScheme="red"
                  variant="outline"
                  disabled={disableMutation.isPending}
                  onClick={() => setDisableDialogOpen(true)}
                >
                  {tDemo("admin.disableBtn", "Disable demo")}
                </Button>
              ) : (
                <Button
                  size="md"
                  colorScheme="blue"
                  disabled={enableMutation.isPending}
                  onClick={() => setEnableDialogOpen(true)}
                >
                  {tDemo("admin.enableBtn", "Enable demo")}
                </Button>
              )}
            </HStack>
          </Flex>

          {s?.enabled && (
            <Flex
              gap={4}
              flexWrap="wrap"
              borderTop="1px solid"
              borderColor="rgba(255,255,255,0.08)"
              pt={4}
            >
              <Box>
                <Text fontSize="xs" color={sc.textMuted}>
                  {tDemo("admin.lastPurgeLabel", "Last purge")}
                </Text>
                <Text fontSize="sm" color={sc.heading} fontFamily="mono">
                  {s.last_purge_at ?? "—"}
                </Text>
              </Box>
              <Box>
                <Text fontSize="xs" color={sc.textMuted}>
                  {tDemo("admin.nextPurgeLabel", "Next purge")}
                </Text>
                <Text fontSize="sm" color={sc.heading} fontFamily="mono">
                  {s.next_purge_at ?? "—"}
                </Text>
              </Box>
              <Box>
                <Text fontSize="xs" color={sc.textMuted}>
                  Visitor cap
                </Text>
                <Text fontSize="sm" color={sc.heading}>
                  {s.visitor_cap}
                </Text>
              </Box>
              <Box>
                <Text fontSize="xs" color={sc.textMuted}>
                  Demo org
                </Text>
                <Text fontSize="xs" color={sc.heading} fontFamily="mono">
                  {s.org_id ?? "—"}
                </Text>
              </Box>
            </Flex>
          )}
        </VStack>
      </VisionCard>

      {/* Tunables */}
      <VisionCard mb={6}>
        <VStack align="stretch" gap={4} p={5}>
          <Heading size="sm" color={sc.heading}>
            {tDemo("admin.purgeIntervalLabel", "Purge interval (hours)")}
          </Heading>
          <Flex gap={3} flexWrap="wrap" align="end">
            <Box flex="1" minW="200px">
              <Text fontSize="xs" color={sc.textMuted} mb={1}>
                {tDemo("admin.purgeIntervalLabel", "Purge interval (hours)")}
              </Text>
              <Input
                size="sm"
                placeholder={String(s?.purge_interval_hours ?? 4)}
                value={draftInterval}
                onChange={(e) => setDraftInterval(e.target.value)}
              />
            </Box>
            <Button
              size="sm"
              colorScheme="blue"
              onClick={applyForm}
              disabled={updateMutation.isPending}
            >
              {t("audit.filter.apply", "Apply")}
            </Button>
          </Flex>

          {/* Banner free-text slots — flank the HH:MM:SS countdown
              in the yellow strip. Empty value clears the override and
              the SPA falls back to its i18n default. */}
          <Heading size="sm" color={sc.heading} mt={2}>
            {tDemo(
              "admin.bannerSlots.title",
              "Banner messages (left + right of timer)",
            )}
          </Heading>
          <Flex gap={3} flexWrap="wrap" align="end">
            <Box flex="1" minW="240px">
              <Text fontSize="xs" color={sc.textMuted} mb={1}>
                {tDemo("admin.bannerSlots.left", "Left of HH:MM:SS")}
              </Text>
              <Input
                size="sm"
                placeholder="TapDPP demo instance will reset in"
                value={draftBannerLeft}
                onChange={(e) => setDraftBannerLeft(e.target.value)}
              />
            </Box>
            <Box flex="1" minW="240px">
              <Text fontSize="xs" color={sc.textMuted} mb={1}>
                {tDemo("admin.bannerSlots.right", "Right of HH:MM:SS")}
              </Text>
              <Input
                size="sm"
                placeholder=""
                value={draftBannerRight}
                onChange={(e) => setDraftBannerRight(e.target.value)}
              />
            </Box>
          </Flex>
          <Text fontSize="xs" color={sc.textMuted}>
            {tDemo(
              "admin.bannerSlots.hint",
              "Save (Apply) propagates to every visitor on the next banner refresh (60s). Leave a slot blank to use the SPA's built-in default.",
            )}
          </Text>

          <Box pt={2}>
            <Button
              size="sm"
              colorScheme="orange"
              variant="outline"
              disabled={!s?.enabled || purgeMutation.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    tDemo(
                      "admin.purgeNowConfirm",
                      "Purge all demo data now? Visitors will lose their session.",
                    ),
                  )
                ) {
                  purgeMutation.mutate()
                }
              }}
            >
              <FiTrash2 style={{ marginRight: 8 }} />
              {tDemo("admin.purgeNowBtn", "Purge demo data now")}
            </Button>
          </Box>
        </VStack>
      </VisionCard>

      {/* Extend timer */}
      <VisionCard mb={6}>
        <VStack align="stretch" gap={3} p={5}>
          <Heading size="sm" color={sc.heading}>
            {tDemo("admin.extend.title", "Extend the auto-reset deadline")}
          </Heading>
          <Text fontSize="xs" color={sc.textMuted}>
            {tDemo(
              "admin.extend.help",
              "Push next_purge_at further into the future. Useful when a live demo is running over and you don't want the auto-purge to wipe state mid-walkthrough.",
            )}
          </Text>
          {s?.next_purge_at && (
            <Text fontSize="xs" color={sc.textMuted}>
              {tDemo("admin.extend.currentLabel", "Currently resets at:")}{" "}
              <Text as="span" color={sc.heading} fontFamily="mono">
                {s.next_purge_at}
              </Text>{" "}
              ({nextResetCountdown})
            </Text>
          )}
          <Flex gap={3} flexWrap="wrap" align="end">
            <Box flex="0 0 140px">
              <Text fontSize="xs" color={sc.textMuted} mb={1}>
                {tDemo("admin.extend.amountLabel", "Add minutes")}
              </Text>
              <Input
                type="number"
                min={1}
                max={10080}
                size="sm"
                value={extendMinutes}
                onChange={(e) => setExtendMinutes(e.target.value)}
              />
            </Box>
            <Button
              size="sm"
              colorScheme="blue"
              disabled={!s?.enabled || extendMutation.isPending}
              onClick={() => applyExtend()}
            >
              {tDemo("admin.extend.apply", "Extend")}
            </Button>
            {/* Common presets so the operator doesn't have to retype
                for the usual "another hour" / "another day" cases. */}
            {[15, 60, 4 * 60, 24 * 60].map((m) => (
              <Button
                key={m}
                size="sm"
                variant="outline"
                disabled={!s?.enabled || extendMutation.isPending}
                onClick={() => applyExtend(m)}
              >
                +
                {m < 60
                  ? `${m}m`
                  : m < 24 * 60
                    ? `${m / 60}h`
                    : `${m / (24 * 60)}d`}
              </Button>
            ))}
          </Flex>
        </VStack>
      </VisionCard>

      {/* Purge log */}
      <VisionCard>
        <Flex justify="space-between" align="center" p={4}>
          <Heading size="md" color={sc.heading}>
            {tDemo("admin.logTitle", "Recent purges")}
          </Heading>
          <VisionButton
            variant="ghost"
            size="sm"
            onClick={() => purgeLogQuery.refetch()}
          >
            <FiRefreshCw />
          </VisionButton>
        </Flex>
        {purgeLogQuery.isLoading ? (
          <Flex justify="center" py={6}>
            <Spinner size="md" color={sc.spinnerColor} />
          </Flex>
        ) : (purgeLogQuery.data ?? []).length === 0 ? (
          <Text color={sc.textMuted} fontSize="sm" px={4} pb={4}>
            {tDemo("admin.logEmpty", "No purges yet.")}
          </Text>
        ) : (
          <Box overflowX="auto">
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("audit.col.time", "Timestamp")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    Reason
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    Stats
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {(purgeLogQuery.data ?? []).map((row) => (
                  <Table.Row key={row.id}>
                    <Table.Cell color={sc.heading} fontSize="sm">
                      {row.at.replace("T", " ").slice(0, 19)}
                    </Table.Cell>
                    <Table.Cell color={sc.heading} fontSize="sm">
                      {(row.metadata.reason as string) ?? "—"}
                    </Table.Cell>
                    <Table.Cell
                      color={sc.textMuted}
                      fontSize="xs"
                      fontFamily="mono"
                    >
                      {JSON.stringify(row.metadata.stats ?? {})}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        )}
      </VisionCard>

      {/* Reader-creation marketing consents */}
      <VisionCard mt={6}>
        <Flex
          justify="space-between"
          align="center"
          p={4}
          gap={3}
          flexWrap="wrap"
        >
          <Flex align="center" gap={3}>
            <IconBox variant="glass" size="md">
              <FiMail fontSize="20px" color={sc.iconColor} />
            </IconBox>
            <Box>
              <Heading size="md" color={sc.heading}>
                {tDemo("admin.consents.title", "Reader sign-up emails")}
              </Heading>
              <Text color={sc.textMuted} fontSize="sm">
                {tDemo(
                  "admin.consents.subtitle",
                  "Email + marketing opt-in for every self-provisioned demo reader.",
                )}
              </Text>
            </Box>
          </Flex>
          <HStack gap={2}>
            <Button
              size="sm"
              colorScheme="blue"
              onClick={downloadConsentsCsv}
              disabled={(consentsQuery.data ?? []).length === 0}
            >
              <FiDownload style={{ marginRight: 8 }} />
              {tDemo("admin.consents.downloadCsv", "Download CSV")}
            </Button>
            <VisionButton
              variant="ghost"
              size="sm"
              onClick={() => consentsQuery.refetch()}
            >
              <FiRefreshCw />
            </VisionButton>
          </HStack>
        </Flex>
        {consentsQuery.isLoading ? (
          <Flex justify="center" py={6}>
            <Spinner size="md" color={sc.spinnerColor} />
          </Flex>
        ) : consentsQuery.isError ? (
          <Text color="red.300" fontSize="sm" px={4} pb={4}>
            {tDemo("admin.consents.error", "Failed to load reader consents.")}
          </Text>
        ) : (consentsQuery.data ?? []).length === 0 ? (
          <Text color={sc.textMuted} fontSize="sm" px={4} pb={4}>
            {tDemo("admin.consents.empty", "No self-provisioned readers yet.")}
          </Text>
        ) : (
          <Box overflowX="auto">
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {tDemo("admin.consents.colEmail", "Email")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {tDemo("admin.consents.colOptIn", "Opt-in")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {tDemo("admin.consents.colRecordedAt", "Recorded at")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {tDemo("admin.consents.colReader", "Reader")}
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {(consentsQuery.data ?? []).map((row) => (
                  <Table.Row key={row.reader_id}>
                    <Table.Cell
                      color={sc.heading}
                      fontSize="sm"
                      fontFamily="mono"
                    >
                      {row.email || "—"}
                    </Table.Cell>
                    <Table.Cell>
                      <Badge
                        colorScheme={row.opt_in ? "green" : "gray"}
                        size="sm"
                      >
                        {row.opt_in
                          ? tDemo("admin.consents.optedIn", "Opted in")
                          : tDemo("admin.consents.optedOut", "Opted out")}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell
                      color={sc.textMuted}
                      fontSize="xs"
                      fontFamily="mono"
                    >
                      {row.opt_in_at
                        ? row.opt_in_at.replace("T", " ").slice(0, 19)
                        : "—"}
                    </Table.Cell>
                    <Table.Cell color={sc.heading} fontSize="sm">
                      {row.reader_name || "—"}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        )}
      </VisionCard>

      {/* Enable-demo info dialog */}
      <DialogRoot
        open={enableDialogOpen}
        onOpenChange={(e) => setEnableDialogOpen(e.open)}
      >
        <DialogBackdrop />
        <DialogContent maxW="lg">
          <DialogHeader>
            <DialogTitle>
              <Heading size="md" color={sc.heading}>
                {tDemo("admin.enableDialog.title", "Enable demo mode?")}
              </Heading>
            </DialogTitle>
            <DialogCloseTrigger />
          </DialogHeader>
          <DialogBody>
            <VStack align="stretch" gap={4}>
              <Box>
                <Heading size="xs" color={sc.heading} mb={2}>
                  {tDemo("admin.enableDialog.willAddTitle", "Will be added")}
                </Heading>
                <VStack
                  as="ul"
                  align="stretch"
                  gap={1}
                  pl={4}
                  listStyleType="disc"
                >
                  <Text as="li" fontSize="sm" color={sc.textMuted}>
                    {tDemo(
                      "admin.enableDialog.willAddDemoOrg",
                      "A protected 'Demo' organisation (kept across purges)",
                    )}
                  </Text>
                  <Text as="li" fontSize="sm" color={sc.textMuted}>
                    {tDemo(
                      "admin.enableDialog.willAddReaders",
                      "2 demo readers (Demo-Reader-A, Demo-Reader-B)",
                    )}
                  </Text>
                  <Text as="li" fontSize="sm" color={sc.textMuted}>
                    {tDemo(
                      "admin.enableDialog.willAddTags",
                      "14 demo tags — 10 UHF (6 temperature, 2 humidity, 2 generic) + 4 NFC tags with NDEF URLs",
                    )}
                  </Text>
                  <Text as="li" fontSize="sm" color={sc.textMuted}>
                    {tDemo(
                      "admin.enableDialog.willAddDpp",
                      "1 'Demo Default Passport' template, pre-bound to every seeded tag — public DPP pages are live immediately at tapdpp.qdat.io/<EPCID-or-UID>",
                    )}
                  </Text>
                  <Text as="li" fontSize="sm" color={sc.textMuted}>
                    {tDemo(
                      "admin.enableDialog.willAddSynth",
                      "Synthesized temperature/humidity readings every minute so the dashboard isn't empty",
                    )}
                  </Text>
                  <Text as="li" fontSize="sm" color={sc.textMuted}>
                    {tDemo(
                      "admin.enableDialog.willAddVisitors",
                      "An ephemeral visitor-session minter — anyone can open the demo without signing up",
                    )}
                  </Text>
                </VStack>
              </Box>

              <Box>
                <Heading size="xs" color="red.300" mb={2}>
                  {tDemo(
                    "admin.enableDialog.willRemoveTitle",
                    "Will be removed / disabled",
                  )}
                </Heading>
                <VStack
                  as="ul"
                  align="stretch"
                  gap={1}
                  pl={4}
                  listStyleType="disc"
                >
                  <Text as="li" fontSize="sm" color={sc.textMuted}>
                    {tDemo(
                      "admin.enableDialog.willRemoveProd",
                      "All tags, readers, commands, readings, events, and alerts owned by non-protected organisations (everything except 'Default' and 'Demo')",
                    )}
                  </Text>
                  <Text as="li" fontSize="sm" color={sc.textMuted}>
                    {tDemo(
                      "admin.enableDialog.willRemoveTokens",
                      "All active API tokens — every PAT is revoked",
                    )}
                  </Text>
                  <Text as="li" fontSize="sm" color={sc.textMuted}>
                    {tDemo(
                      "admin.enableDialog.willRemoveMqtt",
                      "All reader MQTT credentials — readers must be re-issued credentials before they can reconnect",
                    )}
                  </Text>
                  <Text as="li" fontSize="sm" color={sc.textMuted}>
                    {tDemo(
                      "admin.enableDialog.willDisableSignup",
                      "User signup, logo upload, backup/restore, database truncate, and 'Delete Simulation Data' (all locked while demo is on)",
                    )}
                  </Text>
                </VStack>
              </Box>
            </VStack>
          </DialogBody>
          <DialogFooter>
            <DialogActionTrigger asChild>
              <Button variant="outline" size="sm">
                {tDemo("admin.enableDialog.cancel", "Cancel")}
              </Button>
            </DialogActionTrigger>
            <Button
              colorScheme="blue"
              size="sm"
              disabled={enableMutation.isPending}
              onClick={() => {
                enableMutation.mutate()
                setEnableDialogOpen(false)
              }}
            >
              {tDemo("admin.enableDialog.confirm", "Enable demo mode")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>

      {/* Disable-demo info dialog */}
      <DialogRoot
        open={disableDialogOpen}
        onOpenChange={(e) => setDisableDialogOpen(e.open)}
      >
        <DialogBackdrop />
        <DialogContent maxW="lg">
          <DialogHeader>
            <DialogTitle>
              <Heading size="md" color={sc.heading}>
                {tDemo("admin.disableDialog.title", "Disable demo mode?")}
              </Heading>
            </DialogTitle>
            <DialogCloseTrigger />
          </DialogHeader>
          <DialogBody>
            <VStack align="stretch" gap={4}>
              <Box>
                <Heading size="xs" color="red.300" mb={2}>
                  {tDemo(
                    "admin.disableDialog.willRemoveTitle",
                    "Will be deleted",
                  )}
                </Heading>
                <VStack
                  as="ul"
                  align="stretch"
                  gap={1}
                  pl={4}
                  listStyleType="disc"
                >
                  <Text as="li" fontSize="sm" color={sc.textMuted}>
                    {tDemo(
                      "admin.disableDialog.willRemoveTags",
                      "All 14 demo tags + every synthesized reading, event, and alert tied to them",
                    )}
                  </Text>
                  <Text as="li" fontSize="sm" color={sc.textMuted}>
                    {tDemo(
                      "admin.disableDialog.willRemoveReaders",
                      "Both demo readers and any commands routed through them",
                    )}
                  </Text>
                  <Text as="li" fontSize="sm" color={sc.textMuted}>
                    {tDemo(
                      "admin.disableDialog.willRemoveTagTemplate",
                      "The 'Demo Default' tag template",
                    )}
                  </Text>
                  <Text as="li" fontSize="sm" color={sc.textMuted}>
                    {tDemo(
                      "admin.disableDialog.willRemoveDpp",
                      "The 'Demo Default Passport' DPP template — every public DPP page seeded in this demo cycle goes 404",
                    )}
                  </Text>
                  <Text as="li" fontSize="sm" color={sc.textMuted}>
                    {tDemo(
                      "admin.disableDialog.willRemoveVisitors",
                      "Every visitor user account (demo-*@demo.local) + their full audit trail, sessions, and per-resource permissions",
                    )}
                  </Text>
                </VStack>
              </Box>

              <Box>
                <Heading size="xs" color={sc.heading} mb={2}>
                  {tDemo(
                    "admin.disableDialog.consequencesTitle",
                    "Returning to normal operation",
                  )}
                </Heading>
                <VStack
                  as="ul"
                  align="stretch"
                  gap={1}
                  pl={4}
                  listStyleType="disc"
                >
                  <Text as="li" fontSize="sm" color={sc.textMuted}>
                    {tDemo(
                      "admin.disableDialog.consequenceReaders",
                      "Production readers must be re-registered and re-issued MQTT credentials before they can reconnect",
                    )}
                  </Text>
                  <Text as="li" fontSize="sm" color={sc.textMuted}>
                    {tDemo(
                      "admin.disableDialog.consequenceTags",
                      "Tags must be re-ingested (via reader inventory scans or manual creation) — none of the demo seed data survives",
                    )}
                  </Text>
                  <Text as="li" fontSize="sm" color={sc.textMuted}>
                    {tDemo(
                      "admin.disableDialog.consequenceDpp",
                      "Public DPP pages must be rebuilt: create new PassportTemplates and bind them to the new tags (the demo template is wiped)",
                    )}
                  </Text>
                  <Text as="li" fontSize="sm" color={sc.textMuted}>
                    {tDemo(
                      "admin.disableDialog.consequenceTokens",
                      "API tokens revoked during enable are NOT restored — users must mint fresh ones",
                    )}
                  </Text>
                </VStack>
              </Box>

              <Text fontSize="xs" color={sc.textMuted}>
                {tDemo(
                  "admin.disableDialog.footer",
                  "The protected 'Demo' organisation row itself is kept (so re-enabling later is a single click), but everything inside it is wiped.",
                )}
              </Text>
            </VStack>
          </DialogBody>
          <DialogFooter>
            <DialogActionTrigger asChild>
              <Button variant="outline" size="sm">
                {tDemo("admin.disableDialog.cancel", "Cancel")}
              </Button>
            </DialogActionTrigger>
            <Button
              colorScheme="red"
              size="sm"
              disabled={disableMutation.isPending}
              onClick={() => {
                disableMutation.mutate()
                setDisableDialogOpen(false)
              }}
            >
              {tDemo("admin.disableDialog.confirm", "Disable demo mode")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </Box>
  )
}
